import { useState, useEffect, useCallback, useRef } from 'react';
import { Server, RefreshCw, Trash2, Zap, X, Wifi, WifiOff, Plus, FlaskConical, Wrench, RotateCcw, KeyRound, Scissors } from 'lucide-react';
import { vpsAPI, autoTaskAPI } from '../api';
import './VpsManager.css';

const STATUS_LABEL = {
  active: 'Активен',
  blocked: 'Заблокирован',
  building: 'Создаётся',
  checking: 'Проверяется',
  installing: 'Установка',
  // Пул: IP проверен и открыт в ТМ, но VLESS не поставлен и в подписках его нет
  pool: 'В пуле',
};

const STATUS_COLOR = {
  active: 'vps-status-active',
  blocked: 'vps-status-blocked',
  building: 'vps-status-building',
  checking: 'vps-status-building',
  installing: 'vps-status-building',
  pool: 'vps-status-pool',
};

// Показываем только VPS, реально созданные через auto-provisioning API —
// имя вида vpn-{groupId}-{index}-{timestamp} или vpn-{groupId}-{index}-{timestamp}-{seq}
// (uniqueName в vpsManager.js добавляет base36-суффикс при batch — та же конвенция, что
// parseGroupFromName в server/routes/vps.js). Вручную добавленные записи (без паттерна) скрываем.
const API_VPS_NAME_RE = /^vpn-.+-\d+-\d{13}(?:-[a-z0-9]+)?$/;

// Группы записи: правда в groupIds, groupId — зеркало первой группы у старых записей реестра.
const groupsOf = (v) => (Array.isArray(v?.groupIds) ? v.groupIds : (v?.groupId ? [v.groupId] : []));

// Выбор групп чекбоксами: один VPS/CDN-вход может обслуживать несколько групп сразу
// («1 сервер на yhlas и universal»), поэтому селект одной группы здесь не годится.
// Галка применяется сразу — промежуточной кнопки «Сохранить» нет, состояние живёт на сервере.
function GroupPicker({ groups, value, disabled, onChange, title }) {
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const label = selected.length === 0
    ? '— без группы —'
    : selected.map(id => groups[id]?.name || id).join(', ');

  return (
    <span className="vps-group-picker">
      <button
        type="button"
        className="vps-group-select"
        style={selected.length ? undefined : { borderColor: '#f59e0b', color: '#f59e0b' }}
        disabled={disabled}
        title={title || 'Группы: ссылка сервера лежит в файлах каждой отмеченной группы'}
        onClick={() => setOpen(o => !o)}
      >
        {label} ▾
      </button>
      {open && (
        <div className="vps-group-menu">
          {Object.entries(groups || {}).map(([id, g]) => (
            <label key={id} className="vps-group-menu-item">
              <input
                type="checkbox"
                checked={selected.includes(id)}
                disabled={disabled}
                onChange={() => onChange(
                  selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]
                )}
              />
              <span>{g.name || id}</span>
            </label>
          ))}
          <button type="button" className="vps-group-menu-close" onClick={() => setOpen(false)}>Готово</button>
        </div>
      )}
    </span>
  );
}

function VpsManager({ groups, onClose }) {
  const [vpsList, setVpsList] = useState([]);
  const [probeOnline, setProbeOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [reinstallId, setReinstallId] = useState(null);
  // Ключ группы, по которой идёт массовое восстановление ('__all__' — по всем сразу)
  const [restoringAll, setRestoringAll] = useState(null);
  const [error, setError] = useState('');
  const [log, setLog] = useState('');
  const [testIp, setTestIp] = useState('mail.ru');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [autoStatus, setAutoStatus] = useState({ running: false, stopRequested: false });
  const [taskStatus, setTaskStatus] = useState({ running: false, cronActive: false, lastRunAt: null });
  const [serverLogs, setServerLogs] = useState([]);
  const [costs, setCosts] = useState(null);
  const [showCosts, setShowCosts] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualForm, setManualForm] = useState({ ip: '', region: '', groupId: '', sshPassword: '', vpsId: '', provider: 'hostvds2', sshUser: '', sshPort: '', panelName: '', ipv6: '', monthlyCost: '', notes: '', asGcoreOrigin: false });
  // Пул: квота по регионам HostVDS2, выбранный регион раунда и что сейчас крутится.
  // poolGroups — выбор групп на КАЖДОЙ карточке пула отдельно (ключ = vpsId), потому что
  // сервер из пула ещё ничей: группу назначает человек в момент «Настроить VLESS».
  const [quota, setQuota] = useState(null);
  const [poolRegion, setPoolRegion] = useState('');
  const [poolBusy, setPoolBusy] = useState(false);
  const [poolGroups, setPoolGroups] = useState({});
  const [setupId, setSetupId] = useState(null);
  // Gcore: ориджины + созданные CDN-ресурсы. Ресурс стоит денег (тариф Start), поэтому
  // создаётся только по явному нажатию — фонового авто-режима тут нет.
  const [gcore, setGcore] = useState({ configured: false, origins: [], resources: [] });
  const [gcoreBusy, setGcoreBusy] = useState(false);
  const [gcoreGroup, setGcoreGroup] = useState('');
  // На каком сервере поднимать новый вход. Пусто = взять первый готовый ориджин (или создать).
  // Нужен, когда у группы уже есть вход: без выбора второй встал бы на тот же сервер, и смысл
  // второго входа терялся.
  const [gcoreVps, setGcoreVps] = useState('');
  const serverLogRef = useRef(null);

  const groupList = Object.entries(groups || {});
  // cdn-origin (VPS под CDN-фронтингом) в списке не показываем: CDN-панели убраны из UI,
  // а origin-записи в реестре у некоторых ещё лежат — пусть не мешаются среди обычных VPS.
  const apiVpsList = vpsList.filter(v => API_VPS_NAME_RE.test(v.name || '') && v.role !== 'cdn-origin');

  // Два слоя инвентаря. Слой 1 — пул: открытые в ТМ IP, на которых ещё нет VLESS.
  // Слой 2 — рабочие входы, они и раскладываются по группам ниже.
  const poolList = apiVpsList.filter(v => v.status === 'pool');
  const entryList = apiVpsList.filter(v => v.status !== 'pool');

  // Порядковый номер группы из её названия: «Group2-...» → 2, «Gruoup1-Yhlas» → 1.
  // По алфавиту сортировать нельзя — из-за опечатки «Gruoup1» первая группа уезжала в конец,
  // а «Group10» встал бы перед «Group2». Группы без цифры уходят вниз.
  const groupOrder = (name) => {
    const m = /\d+/.exec(name || '');
    return m ? Number(m[0]) : Number.MAX_SAFE_INTEGER;
  };

  // Группировка списка VPS по группам — рисуем заголовок на каждую группу, VPS без группы в конце.
  const groupedVps = (() => {
    const buckets = new Map();
    for (const vps of entryList) {
      // Мультигруппа: сервер показывается в секции КАЖДОЙ своей группы — иначе из списка
      // группы «universal» пропал бы сервер, который её обслуживает вторым назначением.
      const keys = groupsOf(vps);
      for (const key of keys.length ? keys : ['__none__']) {
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(vps);
      }
    }
    return [...buckets.entries()]
      .map(([key, list]) => ({
        key,
        name: key === '__none__' ? 'Без группы' : (groups[key]?.name || key),
        list,
      }))
      .sort((a, b) =>
        (a.key === '__none__') - (b.key === '__none__') ||
        groupOrder(a.name) - groupOrder(b.name) ||
        a.name.localeCompare(b.name)
      );
  })();

  const load = useCallback(async () => {
    setError('');
    try {
      const [vpsRes, probeRes, autoRes, taskRes] = await Promise.allSettled([
        vpsAPI.getAll(),
        vpsAPI.probeStatus(),
        vpsAPI.autoStatus(),
        autoTaskAPI.getStatus(),
      ]);
      if (vpsRes.status === 'fulfilled') {
        const d = vpsRes.value.data;
        setVpsList(Array.isArray(d) ? d : (d.vps || d.items || []));
      }
      if (probeRes.status === 'fulfilled') setProbeOnline(probeRes.value.data.online);
      if (autoRes.status === 'fulfilled') setAutoStatus(autoRes.value.data);
      if (taskRes.status === 'fulfilled') setTaskStatus(taskRes.value.data);
    } catch {
      setError('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Квота тянется отдельно от общего load(): она ходит в API HostVDS по всем шести регионам
  // и заметно медленнее, а в четырёхсекундный автообновляющийся цикл её тащить незачем —
  // меняется она только после раунда.
  const loadQuota = useCallback(async () => {
    try {
      const res = await vpsAPI.getQuota();
      setQuota(res.data);
      // Первый заход — подставляем самый свободный регион: именно там больше всего шансов
      // набрать пачку, а гадать вручную юзеру незачем.
      setPoolRegion(prev => prev || [...(res.data.regions || [])]
        .sort((a, b) => b.free - a.free)[0]?.region || '');
    } catch (err) {
      setError('Квота недоступна: ' + (err.response?.data?.error || err.message));
    }
  }, []);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  // Один раунд поиска. Сервер отвечает 202 сразу — сам раунд (создание + 45с загрузки ОС +
  // probe каждого) идёт минутами, прогресс виден в логе сервера ниже.
  const handlePoolRound = async () => {
    setPoolBusy(true);
    setError('');
    try {
      await vpsAPI.poolRound(poolRegion || null);
    } catch (err) {
      setError('Раунд не запустился: ' + (err.response?.data?.error || err.message));
    } finally {
      // Снимаем блокировку не сразу: раунд идёт в фоне, и повторное нажатие через секунду
      // запустило бы второй заход в тот же регион.
      setTimeout(() => { setPoolBusy(false); loadQuota(); }, 8000);
    }
  };

  const handleSetupPool = async (vps) => {
    const gids = poolGroups[vps.vpsId] || [];
    if (gids.length === 0) {
      setError('Сначала выбери группу для ' + vps.ip);
      return;
    }
    setSetupId(vps.vpsId);
    setError('');
    try {
      await vpsAPI.setupPool(vps.vpsId, gids);
    } catch (err) {
      setError('Настройка не запустилась: ' + (err.response?.data?.error || err.message));
    } finally {
      setTimeout(() => { setSetupId(null); load(); loadQuota(); }, 5000);
    }
  };

  // Auto-refresh VPS list every 4s while panel is open
  useEffect(() => {
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const loadGcore = useCallback(async () => {
    try {
      const { data } = await vpsAPI.getGcore();
      setGcore(data);
    } catch { /* Gcore может быть не настроен — панель просто не покажется */ }
  }, []);

  useEffect(() => { loadGcore(); }, [loadGcore]);

  // Провижининг Gcore идёт минутами (сертификат + раскатка по PoP), ответ приходит сразу 202.
  // Поэтому кнопку не держим заблокированной до конца — только пару секунд от двойного клика,
  // а прогресс читается в логе сервера ниже.
  const handleGcoreProvision = async () => {
    if (!gcoreGroup) { setError('Сначала выбери группу для Gcore'); return; }
    setGcoreBusy(true);
    setError('');
    const onVps = gcoreVps ? ` на сервере ${(gcore.candidates || []).find(c => c.vpsId === gcoreVps)?.ip || gcoreVps}` : '';
    setLog(`Gcore: создаю вход для группы ${groups[gcoreGroup]?.name || gcoreGroup}${onVps}. Раскатка по PoP до 30 минут — следи за логом сервера.`);
    try {
      await vpsAPI.provisionGcoreGroup(gcoreGroup, gcoreVps || undefined);
    } catch (err) {
      setError('Gcore не запустился: ' + (err.response?.data?.error || err.message));
    } finally {
      setTimeout(() => { setGcoreBusy(false); loadGcore(); }, 5000);
    }
  };

  const handleGcoreRefresh = async () => {
    setGcoreBusy(true);
    setError('');
    try {
      const { data } = await vpsAPI.refreshGcore();
      const checked = data.resources?.length || 0;
      const moved = (data.resources || []).filter(r => r.changed).length;
      // «Проверено 0» само по себе выглядит как ошибка. На деле это просто значит, что готовых
      // входов ещё нет: вход попадает в список только когда цепочка дошла до конца (серт +
      // раскатка), а до тех пор проверять нечего.
      setLog(checked === 0
        ? 'Gcore: готовых входов пока нет — проверять нечего. Если вход сейчас создаётся, дождись строки «готово» в логе сервера.'
        : `Gcore: проверено входов ${checked}, ссылок переложено ${moved}`);
      loadGcore();
    } catch (err) {
      setError('Проверка входов не удалась: ' + (err.response?.data?.error || err.message));
    } finally {
      setGcoreBusy(false);
    }
  };

  // Разделить старый вход, за которым стоит несколько серверов: каждому, кроме первого,
  // поднимается свой домен со своим UUID. Ссылка первого сервера не меняется.
  const handleGcoreSplit = async (resourceId, host, count) => {
    if (!confirm(
      `Разделить вход ${host} (серверов ${count})?\n\n` +
      'У каждого сервера будет свой домен и свой UUID. Первый сервер остаётся на этом входе — ' +
      `его ссылка не изменится; остальным ${count - 1} поднимутся новые входы, до 30 мин на каждый.`
    )) return;
    setGcoreBusy(true);
    setError('');
    setLog(`${host}: разделяю вход — следи за логом сервера`);
    try {
      await vpsAPI.splitGcoreEntry(resourceId);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setTimeout(() => { setGcoreBusy(false); loadGcore(); load(); }, 5000);
    }
  };

  const handleGcoreRemoveOrigin = async (resourceId, host, vpsId, ip) => {
    if (!confirm(`Снять ${ip || vpsId} со входа ${host}? Остальные серверы входа продолжат работать.`)) return;
    setGcoreBusy(true);
    setError('');
    try {
      await vpsAPI.removeGcoreOrigin(resourceId, vpsId);
      setLog(`${host}: ${ip || vpsId} снят со входа`);
      loadGcore();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setGcoreBusy(false);
    }
  };

  const handleGcoreDelete = async (resourceId, host) => {
    if (!window.confirm(`Снять Gcore-вход ${host}? Ссылки исчезнут у клиентов этой группы.`)) return;
    setGcoreBusy(true);
    setError('');
    try {
      await vpsAPI.deleteGcoreResource(resourceId);
      setLog(`Gcore: ${host} снят`);
      loadGcore();
    } catch (err) {
      setError('Не удалось снять: ' + (err.response?.data?.error || err.message));
    } finally {
      setGcoreBusy(false);
    }
  };

  const loadLogs = useCallback(async () => {
    try {
      const el = serverLogRef.current;
      // Автоскролл только если пользователь и так уже смотрит на низ лога —
      // иначе не дёргаем его скролл (scrollTop меняем у самого этого блока,
      // не у внешней модалки, чтобы не сбивать скролл всей модалки).
      const wasNearBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 40 : true;
      const res = await vpsAPI.getLogs();
      setServerLogs(res.data);
      if (wasNearBottom) {
        setTimeout(() => {
          if (serverLogRef.current) serverLogRef.current.scrollTop = serverLogRef.current.scrollHeight;
        }, 50);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadLogs();
    const t = setInterval(loadLogs, 3000);
    return () => clearInterval(t);
  }, [loadLogs]);

  const handleDelete = async (vps) => {
    if (!confirm(`Удалить VPS ${vps.ip || vps.vpsId}?`)) return;
    setActionId(vps.vpsId);
    setError('');
    try {
      await vpsAPI.delete(vps.vpsId);
      setLog(`Удалён VPS ${vps.ip}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления');
    } finally {
      setActionId(null);
    }
  };

  const handleRecheck = async (vps) => {
    setActionId(vps.vpsId);
    setError('');
    setLog(`Проверяю ${vps.ip}...`);
    try {
      const res = await vpsAPI.recheck(vps.vpsId);
      const d = res.data;
      if (d.replaced) {
        setLog(`${d.old} заблокирован → заменён на ${d.new}`);
      } else {
        setLog(`${vps.ip}: ${d.packetsReceived}/4 пакетов → ${d.accessible ? 'доступен' : 'заблокирован'}`);
      }
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка проверки');
      setLog('');
    } finally {
      setActionId(null);
    }
  };

  const handleStartGroup = async (groupId) => {
    if (!probeOnline) { setError('ProbeApp офлайн — подключите телефон'); return; }
    if (autoStatus.running) return; // кнопка и так должна быть disabled
    setError('');
    setLog(`Ищу VPS для группы ${groups[groupId]?.name || groupId}...`);
    try {
      await vpsAPI.autoStart([groupId]);
      setAutoStatus(s => ({ ...s, running: true, stopRequested: false, currentGroup: groupId }));
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка запуска');
      setLog('');
    }
  };

  // Ручное добавление уже созданного VPS по IP (без probe/ProbeApp): DNS + VLESS + в группу.
  // Fire-and-forget — прогресс в логе сервера ниже.
  const handleAddManual = async (e) => {
    e?.preventDefault?.();
    const f = manualForm;
    if (!f.ip.trim() || !f.groupId) { setError('Укажите IP и группу'); return; }
    setManualBusy(true);
    setError('');
    try {
      await vpsAPI.addManual({
        ip: f.ip.trim(),
        region: f.region.trim(),
        groupId: f.groupId,
        provider: f.provider,
        vpsId: f.vpsId.trim() || undefined,
        sshPassword: f.sshPassword || undefined,
        sshUser: f.sshUser.trim() || undefined,
        sshPort: f.sshPort.trim() || undefined,
        panelName: f.panelName.trim() || undefined,
        ipv6: f.ipv6.trim() || undefined,
        monthlyCost: f.monthlyCost.trim() || undefined,
        notes: f.notes.trim() || undefined,
        asGcoreOrigin: f.asGcoreOrigin || undefined,
      });
      setLog(`${f.asGcoreOrigin ? 'Настройка ориджина Gcore' : 'Установка VLESS'} на ${f.ip.trim()} запущена — смотри лог сервера ниже`);
      setShowManual(false);
      setManualForm({ ip: '', region: '', groupId: '', sshPassword: '', vpsId: '', provider: 'hostvds2', sshUser: '', sshPort: '', panelName: '', ipv6: '', monthlyCost: '', notes: '', asGcoreOrigin: false });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setManualBusy(false);
    }
  };

  // Обычный авто-провижининг (autoProvisioner, прямой VLESS через ProbeApp): держит ВСЕ
  // группы заполненными до target. ON → старт по всем группам, OFF → стоп. Тот же handler
  // висит и на кнопке «Стоп» под «Создать VPS» — когда провижн идёт, клик останавливает его.
  const handleAutoToggle = async () => {
    setError('');
    try {
      if (autoStatus.running) {
        await vpsAPI.autoStop();
        setAutoStatus(s => ({ ...s, stopRequested: true }));
        setLog('Останавливаю авто-провижининг…');
      } else {
        if (!probeOnline) { setError('ProbeApp офлайн — подключите телефон'); return; }
        const ids = groupList.map(([id]) => id);
        if (!ids.length) { setError('Нет групп для provisioning'); return; }
        await vpsAPI.autoStart(ids);
        setAutoStatus(s => ({ ...s, running: true, stopRequested: false }));
        setLog('Авто-провижининг запущен — держу все группы заполненными. Смотри лог сервера.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleProbeTest = async () => {
    if (!testIp.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await vpsAPI.probeTest(testIp.trim());
      setTestResult({ ok: true, ...res.data });
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestLoading(false);
    }
  };

  const handleReinstall = async (vps) => {
    if (!confirm(`Переустановить протокол на ${vps.ip}?`)) return;
    setReinstallId(vps.vpsId);
    setError('');
    setLog(`Переустанавливаю протокол на ${vps.ip}...`);
    try {
      await vpsAPI.reinstall(vps.vpsId);
      setLog(`${vps.ip}: переустановка запущена, следи за логом`);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка переустановки');
      setLog('');
    } finally {
      setReinstallId(null);
    }
  };

  // Переделать существующий VPS в ориджин Gcore. Прежние прямые ссылки снимаются — сервер
  // меняет роль и как отдельный вход больше не живёт; трафик пойдёт через edge Gcore.
  const handleMakeGcoreOrigin = async (vps) => {
    if (!confirm(
      `Сделать ${vps.ip} ориджином Gcore?\n\n` +
      'Прямые ссылки этого сервера будут сняты из групп, вместо REALITY встанет xray XHTTP на 8080. ' +
      'Потом нажми «Создать вход» в блоке Gcore.'
    )) return;
    setReinstallId(vps.vpsId);
    setError('');
    setLog(`${vps.ip}: делаю ориджином Gcore — следи за логом сервера`);
    try {
      await vpsAPI.makeGcoreOrigin(vps.vpsId);
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось запустить настройку ориджина');
      setLog('');
    } finally {
      setTimeout(() => { setReinstallId(null); load(); loadGcore(); }, 5000);
    }
  };

  // Мультигруппа VPS. Сервер сам разложит ссылки по файлам: в снятые группы не пишет,
  // в добавленные дописывает, общие не трогает.
  const handleSetGroups = async (vps, groupIds) => {
    setActionId(vps.vpsId);
    setError('');
    try {
      const res = await vpsAPI.setGroups(vps.vpsId, groupIds);
      const names = groupIds.map(id => groups[id]?.name || id).join(', ') || 'без группы';
      setLog(`${vps.ip} → ${names} (записано в ${res.data.filesWritten ?? 0} файл(ов))`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      await load();
    } finally {
      setActionId(null);
    }
  };

  // Дозаполнить SSH-доступ у записи, созданной до того, как ручное добавление стало
  // сохранять пароль. Без пароля «Переустановить» упирается в «креды не подошли».
  const handleSetCreds = async (vps) => {
    const sshPassword = prompt(
      `Root-пароль для ${vps.ip}\n(из панели провайдера; сохранится в реестре и будет ` +
      `использован при переустановке)`,
      ''
    );
    if (sshPassword === null) return;
    const pass = sshPassword.trim();
    if (!pass) { setError('Пароль пустой — ничего не сохранил'); return; }

    setActionId(vps.vpsId);
    setError('');
    try {
      await vpsAPI.setCredentials(vps.vpsId, { sshPassword: pass });
      setLog(`${vps.ip}: SSH-пароль сохранён — теперь можно жать «Переустановить»`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionId(null);
    }
  };

  const handleTaskToggle = async () => {
    setError('');
    try {
      if (taskStatus.cronActive) {
        await autoTaskAPI.stop();
        setLog('Часовая проверка остановлена');
      } else {
        if (!probeOnline) { setError('ProbeApp офлайн — подключите телефон'); return; }
        await autoTaskAPI.start();
        setLog('Часовая проверка запущена');
      }
      const res = await autoTaskAPI.getStatus();
      setTaskStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка управления часовой проверкой');
    }
  };

  const handleMonitorRun = async () => {
    setError('');
    setLog('Запускаю цикл проверки...');
    try {
      await autoTaskAPI.run();
      setLog('Цикл проверки запущен');
    } catch (err) {
      setError('Ошибка запуска цикла');
    }
  };

  const handleRestoreUrl = async (vps) => {
    setActionId(vps.vpsId);
    setError('');
    try {
      const res = await vpsAPI.restoreUrl(vps.vpsId);
      setLog(`URL восстановлен в ${res.data.filesWritten} файл(а) группы ${vps.groupId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка восстановления URL');
    } finally {
      setActionId(null);
    }
  };

  // groupKey === null → по всем группам; '__none__' пропускаем — там восстанавливать некуда
  const handleRestoreAll = async (groupKey) => {
    const scope = groupKey ? `группы «${groups[groupKey]?.name || groupKey}»` : 'всех групп';
    if (!window.confirm(`Восстановить URL всех VPS ${scope} в файлы подписок?`)) return;
    setRestoringAll(groupKey || '__all__');
    setError('');
    try {
      const { data } = await vpsAPI.restoreUrlAll(groupKey || undefined);
      setLog(`Восстановлено ${data.restored} VPS → ${data.filesWritten} записей в файлах (пропущено ${data.skipped})`);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка массового восстановления URL');
    } finally {
      setRestoringAll(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content vps-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Server size={16} /> VPS Управление</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 11 }}
              onClick={async () => {
                if (showCosts) { setShowCosts(false); return; }
                const { data } = await vpsAPI.getCosts();
                setCosts(data);
                setShowCosts(true);
              }}
            >
              💰 Расходы
            </button>
            <button onClick={onClose} className="modal-close"><X size={14} /></button>
          </div>
        </div>

        {showCosts && costs && (
          <div style={{ margin: '0 20px 12px', padding: 12, background: '#0f0f23', borderRadius: 8, border: '1px solid #2a2a4a', fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#a0a0c0' }}>Месяц: <b style={{ color: '#fff' }}>{costs.month}</b></span>
              <span style={{ color: '#4ade80', fontWeight: 700, fontSize: 16 }}>${costs.totalUSD}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8, color: '#a0a0c0' }}>
              <span>Активные: <b style={{ color: '#fff' }}>${costs.activeCost}</b></span>
              <span>Удалённые: <b style={{ color: '#fff' }}>${costs.deletedCost}</b></span>
              <span>VPS: <b style={{ color: '#fff' }}>{costs.activeCount}</b></span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(costs.byProvider || {}).map(([p, v]) => (
                <span key={p} style={{ background: '#1a1a3e', padding: '2px 8px', borderRadius: 4, color: '#a78bfa' }}>
                  {p}: <b>${v}</b>
                </span>
              ))}
            </div>
            {costs.activeDetails?.length > 0 && (
              <div style={{ marginTop: 8, borderTop: '1px solid #2a2a4a', paddingTop: 8 }}>
                {costs.activeDetails.map(v => (
                  <div key={v.vpsId} style={{ display: 'flex', justifyContent: 'space-between', color: '#a0a0c0', padding: '2px 0' }}>
                    <span>{v.ip || v.name} <span style={{ color: '#6b7280' }}>· {v.provider} · {Math.round(v.hours)}ч</span></span>
                    <span style={{ color: '#4ade80' }}>${v.cost}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && <div className="alert alert-error" style={{ margin: '0 20px 12px' }}>{error}</div>}

        <div className="modal-body">

          {/* Статус ProbeApp */}
          <div className="vps-section">
            <div className="vps-section-title">ProbeApp</div>
            <div className="vps-probe-row">
              <div className={`vps-probe-badge ${probeOnline ? 'vps-probe-online' : 'vps-probe-offline'}`}>
                {probeOnline ? <Wifi size={13} /> : <WifiOff size={13} />}
                {probeOnline ? 'Онлайн' : 'Офлайн'}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={load} title="Обновить">
                <RefreshCw size={13} />
              </button>

              {/* Авто VPS toggle — обычный авто-провижининг (прямой VLESS через ProbeApp) — right side */}
              <div className="vps-auto-toggle-wrap">
                <span className="vps-auto-label">
                  {autoStatus.running ? (autoStatus.stopRequested ? 'Останавливается…' : 'Авто VPS…') : 'Авто VPS'}
                </span>
                <button
                  className={`vps-auto-toggle ${autoStatus.running ? 'vps-auto-on' : ''}`}
                  onClick={handleAutoToggle}
                  disabled={!probeOnline && !autoStatus.running}
                  title={autoStatus.running ? 'Выключить авто-провижининг' : 'Включить авто-провижининг (держать все группы заполненными)'}
                >
                  <span className="vps-auto-knob" />
                </button>
              </div>
            </div>
          </div>

          {/* Probe Test */}
          <div className="vps-section">
            <div className="vps-section-title">Probe Test</div>
            <div className="vps-test-row">
              <input
                className="vps-test-input"
                value={testIp}
                onChange={e => { setTestIp(e.target.value); setTestResult(null); }}
                placeholder="IP или домен (mail.ru)"
                onKeyDown={e => e.key === 'Enter' && !testLoading && handleProbeTest()}
              />
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleProbeTest}
                disabled={testLoading || !probeOnline || !testIp.trim()}
                title={!probeOnline ? 'ProbeApp офлайн' : ''}
              >
                {testLoading ? '...' : <><FlaskConical size={13} /> Тест</>}
              </button>
            </div>
            {testResult && (
              <div className={`vps-test-result ${testResult.ok && testResult.accessible ? 'vps-test-ok' : 'vps-test-fail'}`}>
                {testResult.error
                  ? `Ошибка: ${testResult.error}`
                  : `${testResult.ip} — ${testResult.accessible ? '✓ Доступен' : '✗ Заблокирован'} (${testResult.packetsReceived}/4 пакетов)`
                }
              </div>
            )}
          </div>

          {/* Часовая проверка (autoTask cron) */}
          <div className="vps-section">
            <div className="vps-section-title">Часовая проверка</div>
            <div className="vps-monitor-row">
              <span className="vps-monitor-status">
                {taskStatus.cronActive ? '● Включена' : '○ Выключена'}
                {taskStatus.lastRunAt && (
                  <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 11 }}>
                    послед. прогон: {new Date(taskStatus.lastRunAt).toLocaleTimeString()}
                  </span>
                )}
              </span>
              <div className="vps-monitor-actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button className="btn btn-secondary btn-sm" onClick={handleMonitorRun} title="Запустить цикл сейчас">
                  <Zap size={13} /> Сейчас
                </button>
                <button
                  className={`vps-auto-toggle ${taskStatus.cronActive ? 'vps-auto-on' : ''}`}
                  onClick={handleTaskToggle}
                  title={taskStatus.cronActive ? 'Выключить часовую проверку' : 'Включить часовую проверку'}
                >
                  <span className="vps-auto-knob" />
                </button>
              </div>
            </div>
          </div>

          {/* Создать VPS по группе */}
          {groupList.length > 0 && (
            <div className="vps-section">
              <div className="vps-section-title">Создать VPS</div>
              <div className="vps-provision-list">
                {groupList.map(([id, g]) => (
                  <button
                    key={id}
                    className="btn btn-secondary btn-sm vps-provision-btn"
                    disabled={autoStatus.running || !probeOnline}
                    title={autoStatus.running ? 'Уже выполняется provisioning — сначала остановите' : ''}
                    onClick={() => handleStartGroup(id)}
                  >
                    <Plus size={12} />
                    {g.name}
                  </button>
                ))}
              </div>
              {autoStatus.running && (
                <div className="vps-provision-status">
                  <span>
                    {autoStatus.stopRequested
                      ? 'Останавливается…'
                      : `Идёт поиск VPS — группа "${groups[autoStatus.currentGroup]?.name || autoStatus.currentGroup || '...'}", попытка ${autoStatus.attempt}`}
                  </span>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={autoStatus.stopRequested}
                    onClick={handleAutoToggle}
                  >
                    Стоп
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Gcore-фронтинг. Панель показывается только когда есть токен: без него всё равно
              ничего не создать, а пустой блок сбивает с толку. */}
          {gcore.configured && groupList.length > 0 && (
            <div className="vps-section">
              <div className="vps-section-title">Gcore</div>
              <div className="vps-provision-list" style={{ alignItems: 'center', gap: 8 }}>
                <select
                  className="input input-sm"
                  value={gcoreGroup}
                  disabled={gcoreBusy}
                  onChange={e => setGcoreGroup(e.target.value)}
                >
                  <option value="">Группа…</option>
                  {groupList.map(([id, g]) => <option key={id} value={id}>{g.name}</option>)}
                </select>
                <select
                  className="input input-sm"
                  value={gcoreVps}
                  disabled={gcoreBusy}
                  title="На каком сервере поднять вход. «Авто» берёт СВОБОДНЫЙ ориджин (без входа), а если такого нет — создаёт новый сервер."
                  onChange={e => setGcoreVps(e.target.value)}
                >
                  <option value="">Сервер: авто</option>
                  {/* Серверы с уже висящим входом не показываем: у каждого свой вход и свой UUID,
                      второй вход на том же сервере поднять нельзя. */}
                  {(gcore.candidates || [])
                    .filter(c => !(gcore.resources || []).some(r => (r.origins || []).some(o => o.vpsId === c.vpsId)))
                    .map(c => (
                      <option key={c.vpsId} value={c.vpsId}>
                        {c.ip}{c.role === 'gcore-origin' ? ' (ориджин)' : ''}
                      </option>
                    ))}
                </select>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={gcoreBusy || !gcoreGroup}
                  title="Один вход = один сервер. Создаёт CDN-ресурс Gcore на выбранный сервер и вешает ссылку в файлы группы (до 30 мин). Повторное нажатие на ту же группу даёт следующий домен: gt1, gt2…"
                  onClick={handleGcoreProvision}
                >
                  <Plus size={12} /> Создать вход
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={gcoreBusy}
                  title="Проверить все входы из ТМ и переложить ссылки на живые адреса. Ресурс не пересоздаётся."
                  onClick={handleGcoreRefresh}
                >
                  <RefreshCw size={12} /> Проверить входы
                </button>
              </div>

              {gcore.origins.length === 0 && (
                <div className="vps-provision-status">
                  Ориджинов нет — первый создастся сам при первом входе.
                </div>
              )}

              {gcore.resources.length > 0 && (
                <div className="vps-provision-list" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, marginTop: 8 }}>
                  {gcore.resources.map(r => (
                    <div key={r.resourceId} style={{ borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                        <span>
                          {r.host} — {groups[r.groupId]?.name || r.groupId || 'без группы'} · ссылок {r.links}
                          {(r.origins?.length || 0) === 1
                            ? ` · сервер ${r.origins[0].ip || r.origins[0].fqdn}`
                            : ` · серверов ${r.origins?.length || 0}`}
                        </span>
                        <span style={{ display: 'flex', gap: 4 }}>
                          {/* Наследие старой схемы «вход на группу»: за входом несколько серверов
                              под общим UUID. Разделяем на «вход = сервер». */}
                          {(r.origins?.length || 0) > 1 && (
                            <button
                              className="btn btn-secondary btn-sm"
                              disabled={gcoreBusy}
                              title="Дать каждому серверу свой домен и свой UUID (до 30 мин на сервер)"
                              onClick={() => handleGcoreSplit(r.resourceId, r.host, r.origins.length)}
                            >
                              <Scissors size={12} /> Разделить
                            </button>
                          )}
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={gcoreBusy}
                            title="Снять вход целиком: ссылки, CNAME, ресурс"
                            onClick={() => handleGcoreDelete(r.resourceId, r.host)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </span>
                      </div>

                      {/* Серверы за этим входом. Норма — один; несколько бывает только у входов,
                          созданных до перехода на схему «один вход = один сервер». */}
                      {(r.origins || []).map(o => (
                        <div key={o.fqdn} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#6b7280', paddingLeft: 12 }}>
                          <span>└ {o.ip || o.fqdn}</span>
                          {o.vpsId && (r.origins.length > 1) && (
                            <button
                              className="btn btn-secondary btn-sm"
                              disabled={gcoreBusy}
                              title="Снять этот сервер со входа"
                              onClick={() => handleGcoreRemoveOrigin(r.resourceId, r.host, o.vpsId, o.ip)}
                            >
                              <X size={10} />
                            </button>
                          )}
                        </div>
                      ))}

                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Добавить VPS вручную по IP — без probe/ProbeApp (открытый IP найден руками) */}
          {groupList.length > 0 && (
            <div className="vps-section">
              <div className="vps-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Добавить VPS вручную</span>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowManual(v => !v)}>
                  <Plus size={12} /> {showManual ? 'Скрыть' : 'По IP'}
                </button>
              </div>
              {showManual && (
                <form onSubmit={handleAddManual} className="vps-manual-form" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      className="input"
                      style={{ flex: '1 1 140px' }}
                      placeholder="IP (46.8.71.176)"
                      value={manualForm.ip}
                      onChange={e => setManualForm(f => ({ ...f, ip: e.target.value }))}
                    />
                    <input
                      className="input"
                      style={{ flex: '1 1 120px' }}
                      placeholder="Регион (eu-north1)"
                      value={manualForm.region}
                      onChange={e => setManualForm(f => ({ ...f, region: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <select
                      className="input"
                      style={{ flex: '1 1 140px' }}
                      value={manualForm.groupId}
                      onChange={e => setManualForm(f => ({ ...f, groupId: e.target.value }))}
                    >
                      <option value="">— группа —</option>
                      {groupList.map(([id, g]) => (
                        <option key={id} value={id}>{g.name}</option>
                      ))}
                    </select>
                    <select
                      className="input"
                      style={{ flex: '1 1 120px' }}
                      value={manualForm.provider}
                      onChange={e => setManualForm(f => ({ ...f, provider: e.target.value }))}
                    >
                      <option value="hostvds2">hostvds2</option>
                      <option value="hostvds">hostvds</option>
                      <option value="vultr">vultr</option>
                      {/* upcloud выбирает протокол: на нём ставится Shadowsocks (TCP/443),
                          потому что триальный фаервол UpCloud режет UDP и QUIC/h3 не доходит */}
                      <option value="upcloud">upcloud (Shadowsocks)</option>
                      <option value="manual">manual</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      className="input"
                      type="password"
                      style={{ flex: '1 1 140px' }}
                      placeholder="root-пароль (если по паролю)"
                      value={manualForm.sshPassword}
                      onChange={e => setManualForm(f => ({ ...f, sshPassword: e.target.value }))}
                    />
                    <input
                      className="input"
                      style={{ flex: '1 1 120px' }}
                      placeholder="ID сервера (для удаления, необязат.)"
                      value={manualForm.vpsId}
                      onChange={e => setManualForm(f => ({ ...f, vpsId: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      className="input"
                      style={{ flex: '1 1 90px' }}
                      placeholder="SSH-логин (root)"
                      value={manualForm.sshUser}
                      onChange={e => setManualForm(f => ({ ...f, sshUser: e.target.value }))}
                    />
                    <input
                      className="input"
                      style={{ flex: '0 1 80px' }}
                      placeholder="Порт (22)"
                      value={manualForm.sshPort}
                      onChange={e => setManualForm(f => ({ ...f, sshPort: e.target.value }))}
                    />
                    <input
                      className="input"
                      style={{ flex: '1 1 140px' }}
                      placeholder="Имя в панели (rabocly-server-cds8)"
                      value={manualForm.panelName}
                      onChange={e => setManualForm(f => ({ ...f, panelName: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      className="input"
                      style={{ flex: '1 1 160px' }}
                      placeholder="IPv6 (необязат.)"
                      value={manualForm.ipv6}
                      onChange={e => setManualForm(f => ({ ...f, ipv6: e.target.value }))}
                    />
                    <input
                      className="input"
                      style={{ flex: '0 1 110px' }}
                      placeholder="$/мес (1.49)"
                      value={manualForm.monthlyCost}
                      onChange={e => setManualForm(f => ({ ...f, monthlyCost: e.target.value }))}
                    />
                  </div>
                  <input
                    className="input"
                    placeholder="Заметка (логин в панель, дата оплаты, что угодно)"
                    value={manualForm.notes}
                    onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={manualForm.asGcoreOrigin}
                      onChange={e => setManualForm(f => ({ ...f, asGcoreOrigin: e.target.checked }))}
                    />
                    Ориджин Gcore (не прямой вход)
                  </label>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={manualBusy}>
                    <Plus size={12} /> {manualBusy
                      ? 'Запускаю…'
                      : (manualForm.asGcoreOrigin ? 'Поставить ориджин Gcore' : 'Поставить VLESS и добавить')}
                  </button>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    Для уже созданного сервера с открытым IP. ProbeApp не нужен: заходит по SSH, ставит VLESS,
                    создаёт DNS и кидает ссылку в группу. Прогресс — в логе сервера ниже. Установка идёт 2-5 мин.
                    Все введённые данные сохраняются в реестре — пароль понадобится при «Переустановить».
                  </div>
                  {manualForm.asGcoreOrigin && (
                    <div style={{ fontSize: 11, color: '#f59e0b' }}>
                      Ориджин: ставится xray XHTTP на 8080 без TLS, порт запирается на подсети Gcore.
                      Ссылка в группу НЕ добавляется — клиент ходит через edge. Дальше нажми «Создать вход»
                      в блоке Gcore. IP этого сервера может быть закрыт в ТМ, для ориджина это не важно.
                    </div>
                  )}
                </form>
              )}
            </div>
          )}

          {/* Лог */}
          {log && <div className="vps-log">{log}</div>}

          {/* Лог сервера */}
          <div className="vps-section">
            <div className="vps-section-title">
              Лог сервера
              <button className="btn btn-ghost btn-sm btn-icon" onClick={loadLogs} style={{ marginLeft: 8 }}>
                <RefreshCw size={12} />
              </button>
            </div>
            <div className="vps-server-log" ref={serverLogRef}>
              {serverLogs.length === 0 ? (
                <span className="vps-log-empty">Нет записей</span>
              ) : (
                serverLogs.map((l, i) => (
                  <div key={i} className="vps-log-line">
                    <span className="vps-log-time">{new Date(l.t).toLocaleTimeString()}</span>
                    <span>{l.msg}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Список VPS */}
          <div className="vps-section">
            <div className="vps-section-title">
              VPS ({apiVpsList.length})
              <button className="btn btn-ghost btn-sm btn-icon" onClick={load} style={{ marginLeft: 8 }}>
                <RefreshCw size={12} />
              </button>
              {[
                { label: 'Vultr',     fn: () => vpsAPI.syncVultr() },
                { label: 'HostVDS2',  fn: () => vpsAPI.syncHostvds2() },
              ].map(({ label, fn }) => (
                <button
                  key={label}
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 8, fontSize: 11 }}
                  onClick={async () => {
                    setError('');
                    try {
                      const { data } = await fn();
                      await load();
                      setError(`${label}: импортировано ${data.total}`);
                    } catch (e) {
                      setError(e.response?.data?.error || e.message);
                    }
                  }}
                >
                  ↓ Синк с {label}
                </button>
              ))}
              <button
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 8, fontSize: 11 }}
                disabled={!!restoringAll}
                onClick={() => handleRestoreAll(null)}
                title="Восстановить URL всех VPS во все файлы их групп"
              >
                {restoringAll === '__all__' ? '...' : <><RotateCcw size={11} /> Восстановить все URL</>}
              </button>
            </div>

            {/* ── СЛОЙ 1: пул открытых IP без VLESS ────────────────────────── */}
            <div className="vps-pool">
              <div className="vps-pool-head">
                <span className="vps-pool-title">
                  Пул — открытые в ТМ IP без VLESS
                  <span className="vps-group-count">{poolList.length}</span>
                </span>
                <div className="vps-pool-controls">
                  <select
                    className="vps-group-select"
                    value={poolRegion}
                    onChange={(e) => setPoolRegion(e.target.value)}
                    disabled={poolBusy}
                    title="Регион HostVDS2. Квота у них своя в каждом регионе"
                  >
                    {(quota?.regions || []).map(r => (
                      <option key={r.region} value={r.region} disabled={r.free === 0}>
                        {r.region} — свободно {r.free}/{r.max}{r.error ? ' (ошибка)' : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handlePoolRound}
                    disabled={poolBusy || !probeOnline || !poolRegion}
                    title={probeOnline
                      ? 'Забить все свободные слоты региона, проверить каждый IP через телефон, открытые оставить в пуле'
                      : 'ProbeApp офлайн — проверять IP нечем'}
                  >
                    {poolBusy ? 'Раунд идёт...' : 'Искать VPS'}
                  </button>
                </div>
              </div>

              {poolList.length === 0 ? (
                <p className="vps-empty">
                  Пул пуст. «Искать VPS» создаст столько серверов, сколько разрешает квота региона,
                  проверит каждый через ProbeApp и оставит здесь только открытые. Закрытые удаляются сразу.
                </p>
              ) : (
                <div className="vps-list">
                  {poolList.map((vps) => (
                    <div key={vps.vpsId} className="vps-item">
                      <div className="vps-item-info">
                        <span className={`vps-status-badge ${STATUS_COLOR[vps.status] || ''}`}>
                          {STATUS_LABEL[vps.status] || vps.status}
                        </span>
                        <span className="vps-item-ip">{vps.ip || '—'}</span>
                        <span className="vps-item-meta">{vps.region}</span>
                      </div>
                      <div className="vps-item-actions">
                        <GroupPicker
                          groups={groups}
                          value={poolGroups[vps.vpsId] || []}
                          disabled={setupId === vps.vpsId}
                          title="В какие группы уйдёт ссылка после настройки"
                          onChange={(gids) => setPoolGroups(prev => ({ ...prev, [vps.vpsId]: gids }))}
                        />
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleSetupPool(vps)}
                          disabled={setupId === vps.vpsId}
                          title="Поставить VLESS+REALITY и положить ссылку в файлы выбранных групп"
                        >
                          {setupId === vps.vpsId ? '...' : 'Настроить VLESS'}
                        </button>
                        <button
                          className="btn btn-danger btn-sm btn-icon"
                          onClick={() => handleDelete(vps)}
                          disabled={actionId === vps.vpsId}
                          title="Удалить сервер у провайдера"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── СЛОЙ 2: рабочие входы, разложенные по группам ────────────── */}
            {loading ? (
              <p className="vps-empty">Загрузка...</p>
            ) : entryList.length === 0 ? (
              <p className="vps-empty">Нет рабочих входов</p>
            ) : (
              <div className="vps-grouped">
                {groupedVps.map(({ key, name, list }) => (
                  <div key={key} className="vps-group">
                    <div className="vps-group-header">
                      <span className="vps-group-name">{name}</span>
                      <span className="vps-group-count">{list.length}</span>
                      {key !== '__none__' && (
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          style={{ marginLeft: 8 }}
                          disabled={!!restoringAll}
                          onClick={() => handleRestoreAll(key)}
                          title={`Восстановить URL всех VPS группы «${name}»`}
                        >
                          {restoringAll === key ? '...' : <RotateCcw size={12} />}
                        </button>
                      )}
                    </div>
                    <div className="vps-list">
                {list.map((vps) => (
                  <div key={vps.vpsId} className="vps-item">
                    <div className="vps-item-info">
                      <span className={`vps-status-badge ${STATUS_COLOR[vps.status] || ''}`}>
                        {STATUS_LABEL[vps.status] || vps.status}
                      </span>
                      <span className="vps-item-ip">{vps.ip || '—'}</span>
                      <span className="vps-item-meta">
                        {vps.region} ·{' '}
                        {/* Выбор групп доступен ВСЕГДА, а не только у VPS без группы: и переносить,
                            и добавлять вторую группу нужно уже после назначения первой.
                            Без группы подсвечен оранжевым — это состояние требует внимания. */}
                        <GroupPicker
                          groups={groups}
                          value={groupsOf(vps)}
                          disabled={actionId === vps.vpsId}
                          onChange={(ids) => handleSetGroups(vps, ids)}
                        />
                      </span>
                    </div>
                    <div className="vps-item-actions">
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        disabled={actionId === vps.vpsId || (!vps.vlessUrl && !vps.wsUrl)}
                        onClick={() => handleRestoreUrl(vps)}
                        title={(vps.vlessUrl || vps.wsUrl) ? 'Восстановить URL в файлах группы' : 'Нет сохранённого URL'}
                      >
                        {actionId === vps.vpsId ? '...' : <RotateCcw size={12} />}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        disabled={actionId === vps.vpsId || !vps.ip}
                        onClick={() => handleSetCreds(vps)}
                        title={vps.hasSshPassword
                          ? 'SSH-пароль сохранён — нажми, чтобы заменить'
                          : 'SSH-пароля нет: переустановка пойдёт по ключу. Нажми, чтобы задать пароль'}
                        style={vps.hasSshPassword ? undefined : { color: '#f59e0b' }}
                      >
                        {actionId === vps.vpsId ? '...' : <KeyRound size={12} />}
                      </button>
                      <button
                        className="btn btn-warning btn-sm btn-icon"
                        disabled={reinstallId === vps.vpsId || actionId === vps.vpsId || !vps.ip}
                        onClick={() => handleReinstall(vps)}
                        title="Переустановить протокол"
                      >
                        {reinstallId === vps.vpsId ? '...' : <Wrench size={12} />}
                      </button>
                      {gcore.configured && (
                        <button
                          className="btn btn-secondary btn-sm btn-icon"
                          disabled={reinstallId === vps.vpsId || actionId === vps.vpsId || !vps.ip}
                          onClick={() => handleMakeGcoreOrigin(vps)}
                          title="Сделать ориджином Gcore (прямые ссылки этого сервера снимутся)"
                        >
                          {reinstallId === vps.vpsId ? '...' : <Server size={12} />}
                        </button>
                      )}
                      <button
                        className="btn btn-secondary btn-sm btn-icon"
                        disabled={actionId === vps.vpsId || !probeOnline || !vps.ip}
                        onClick={() => handleRecheck(vps)}
                        title={!vps.ip ? 'IP ещё не назначен' : 'Проверить IP'}
                      >
                        {actionId === vps.vpsId ? '...' : <RefreshCw size={12} />}
                      </button>
                      <button
                        className="btn btn-danger btn-sm btn-icon"
                        disabled={actionId === vps.vpsId}
                        onClick={() => handleDelete(vps)}
                        title="Удалить"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Закрыть</button>
        </div>
      </div>
    </div>
  );
}

export default VpsManager;
